import { queryClient } from '@core/query'
import { FollowQueryKey } from './queryKeys'

import type { TFollowStatistics } from '../hooks/useFollowStatistics'

export type TFollowSnapshot = {
    isFollowing?: boolean
    targetStats?: TFollowStatistics
    myStats?: TFollowStatistics
}

const adjustCount = (username: string, key: keyof TFollowStatistics, delta: number) => {
    queryClient.setQueryData<TFollowStatistics>(
        FollowQueryKey.followStatistics(username),
        previous =>
            previous ? { ...previous, [key]: Math.max(0, previous[key] + delta) } : previous,
    )
}

/** Snapshot the follow flag + both counts so an optimistic update can be rolled back on error. */
export const snapshotFollowState = (username: string, myUsername: string): TFollowSnapshot => ({
    isFollowing: queryClient.getQueryData<boolean>(FollowQueryKey.followCheck(username)),
    targetStats: queryClient.getQueryData<TFollowStatistics>(
        FollowQueryKey.followStatistics(username),
    ),
    myStats: queryClient.getQueryData<TFollowStatistics>(
        FollowQueryKey.followStatistics(myUsername),
    ),
})

/**
 * Optimistically apply a follow (`isFollowing: true`, `+1`) or unfollow (`false`, `-1`) to the
 * follow flag and both counts — the target's followers and the viewer's following.
 */
export const applyFollowState = (username: string, myUsername: string, isFollowing: boolean) => {
    const delta = isFollowing ? 1 : -1

    queryClient.setQueryData<boolean>(FollowQueryKey.followCheck(username), isFollowing)
    adjustCount(username, 'followers_count', delta)
    adjustCount(myUsername, 'following_count', delta)
}

export const restoreFollowState = (
    username: string,
    myUsername: string,
    snapshot: TFollowSnapshot,
) => {
    // setQueryData ignores an `undefined` value (react-query bails), so a flag that was uncached at
    // mutate time can't be rolled back by writing undefined — drop the optimistic entry instead.
    if (snapshot.isFollowing === undefined) {
        queryClient.removeQueries({ queryKey: FollowQueryKey.followCheck(username), exact: true })
    } else {
        queryClient.setQueryData(FollowQueryKey.followCheck(username), snapshot.isFollowing)
    }
    queryClient.setQueryData(FollowQueryKey.followStatistics(username), snapshot.targetStats)
    queryClient.setQueryData(FollowQueryKey.followStatistics(myUsername), snapshot.myStats)
}
